import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  CN01_WORKFLOW_STEPS,
  completedStepCountFromStatus,
  isCn01Rejected,
} from '../cn01StatusUtils';
import { theme } from '~/styles/theme';

const COMPLETE_COLOR = theme.palette.primary.main;
const PENDING_LINE = '#e0e0e0';
const PENDING_CIRCLE = '#bdbdbd';
const ICON_COLOR = theme.palette.primary.dark;
const REJECTED_COLOR = '#f44336';

type Cn01StatusTrackerProps = {
  status: string;
};

export function Cn01StatusTracker({ status }: Cn01StatusTrackerProps) {
  const isRejected = isCn01Rejected(status);
  const completedCount = completedStepCountFromStatus(status);
  const stepCount = CN01_WORKFLOW_STEPS.length;
  const completeColor = isRejected ? REJECTED_COLOR : COMPLETE_COLOR;
  const activeIconColor = isRejected ? REJECTED_COLOR : ICON_COLOR;

  return (
    <Box sx={{ width: '100%', pt: 1 }}>
      {/* Progress line + nodes */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: { xs: 0.5, sm: 2 },
          mb: 2,
        }}
      >
        {CN01_WORKFLOW_STEPS.map((_, index) => {
          const isComplete = index < completedCount;
          const isLast = index === stepCount - 1;
          const segmentComplete = index < completedCount;

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: isLast ? '0 0 auto' : 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isComplete ? completeColor : 'transparent',
                  border: isComplete ? 'none' : `2px solid ${PENDING_CIRCLE}`,
                }}
              >
                {isComplete ? (
                  isRejected && isLast ? (
                    <CloseIcon sx={{ fontSize: 18, color: '#fff' }} />
                  ) : (
                    <CheckIcon sx={{ fontSize: 18, color: '#fff' }} />
                  )
                ) : null}
              </Box>
              {!isLast ? (
                <Box
                  sx={{
                    flex: 1,
                    height: 4,
                    mx: 0.5,
                    borderRadius: 2,
                    bgcolor: segmentComplete ? completeColor : PENDING_LINE,
                    minWidth: 8,
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>

      {/* Step labels + icons */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${stepCount}, 1fr)`,
          gap: 1,
          textAlign: 'center',
        }}
      >
        {CN01_WORKFLOW_STEPS.map((step, index) => {
          const isComplete = index < completedCount;
          const { Icon, label } = step;
          return (
            <Box key={label} sx={{ minWidth: 0, px: 0.5 }}>
              <Icon
                sx={{
                  fontSize: 32,
                  color: isComplete ? activeIconColor : PENDING_CIRCLE,
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: isComplete ? 600 : 400,
                  color: isComplete
                    ? isRejected
                      ? REJECTED_COLOR
                      : 'text.primary'
                    : 'text.secondary',
                  lineHeight: 1.3,
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
